import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useDeleteSubject, useSubject } from '../../api/subjects';
import { useEnumLabel } from '../../api/enums';
import { useIsAdmin } from '../../api/me';
import { isApiError } from '../../api/errors';
import { Breadcrumbs, Button, ButtonGroup, ButtonLink, ConfirmDialog, ErrorSummary, SummaryList, Tabs } from '../../components/idsk';
import { useNavigateWithToast } from '../../components/common/navigateWithToast';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { formatDateTime, formatPsc } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { LoadingPage } from '../common/LoadingPage';
import { EntityHistory } from '../history/EntityHistory';

export function SubjectDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const admin = useIsAdmin();
  const label = useEnumLabel();
  const query = useSubject(id);
  const remove = useDeleteSubject();
  const navigateWithToast = useNavigateWithToast();
  const [confirming, setConfirming] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  usePageTitle([query.data?.name ?? t('common.loading'), t('subjects.title')]);

  if (query.error) return <QueryError error={query.error} asPage />;
  if (query.isPending) return <LoadingPage />;
  const s = query.data;

  const onDelete = async () => {
    setConfirming(false);
    try {
      await remove.mutateAsync({ id: s.id, version: s.version });
      navigateWithToast('/subjekty', t('subjects.deleted', { name: s.name }));
    } catch (e) {
      setDeleteError(isApiError(e) && e.isVersionConflict ? t('errors.versionConflict') : isApiError(e) ? e.message : t('errors.unexpected'));
    }
  };

  const details = (
    <>
      <h2 className="govuk-heading-m">{t('subjects.detailsHeading')}</h2>
      <SummaryList
        rows={[
          ...(admin ? [{ key: t('common.id'), value: <span className="app-mono">{s.id}</span> }] : []),
          { key: t('subjects.fields.name'), value: s.name },
          { key: t('subjects.fields.eduid'), value: s.eduid },
          { key: t('subjects.fields.type'), value: label('institutionTypes', s.type) },
          {
            key: t('subjects.fields.address'),
            value: (
              <>
                {s.address.street} {s.address.houseNumber}
                <br />
                {formatPsc(s.address.postalCode)} {s.address.municipality}
              </>
            ),
          },
          { key: t('subjects.fields.region'), value: label('regions', s.region) },
          { key: t('subjects.fields.district'), value: s.district ? label('districts', s.district) : t('subjects.districtNotProvided') },
          { key: t('common.updatedAt'), value: `${formatDateTime(s.lastModifiedAt)} (${s.lastModifiedBy})` },
        ]}
      />
    </>
  );

  return (
    <>
      <Breadcrumbs items={[{ text: t('nav.home'), to: '/' }, { text: t('subjects.title'), to: '/subjekty' }, { text: s.name }]} />
      {deleteError && <ErrorSummary title={t('subjects.deleteFailed')} errors={[{ text: deleteError }]} />}
      <PageHeading caption={label('institutionTypes', s.type)}>{s.name}</PageHeading>
      {admin && (
        <ButtonGroup>
          <ButtonLink to={`/subjekty/${s.id}/upravit`} variant="secondary">
            {t('common.edit')}
          </ButtonLink>
          <Button variant="warning" onClick={() => setConfirming(true)}>
            {t('common.delete')}
          </Button>
        </ButtonGroup>
      )}
      {admin ? (
        <Tabs
          title={t('common.contents')}
          items={[
            { id: 'udaje', label: t('subjects.tabs.details'), panel: details },
            { id: 'historia', label: t('history.tab'), panel: <EntityHistory entity="subjects" id={s.id} /> },
          ]}
        />
      ) : (
        details
      )}
      <ConfirmDialog
        open={confirming}
        title={t('subjects.confirmDelete.title')}
        confirmText={t('subjects.confirmDelete.confirm')}
        danger
        onConfirm={() => void onDelete()}
        onCancel={() => setConfirming(false)}
      >
        <p className="govuk-body">{t('subjects.confirmDelete.body', { name: s.name })}</p>
      </ConfirmDialog>
    </>
  );
}
